/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Battery, Wifi, MapPin, Thermometer, AlertTriangle, CheckCircle, XCircle, Activity, Signal, Search, LogOut, User, Settings, ChevronDown, Eye, EyeOff } from 'lucide-react';
import dataService from '../services/dataService';
import Logo from './Logo';
import LocationHistory from './LocationHistory';
import LocationMap from './LocationMap';
import CustomDropdown from './CustomDropdown';
import SensorDetail from './SensorDetail';

// Hızlı açılış için kesin fallback IMEI (Firebase tekne listesi 0 dönerse)
const HARD_FALLBACK_IMEI = '351516178484168';

const Dashboard = ({ onLogout }) => {
  const [selectedBoat, setSelectedBoat] = useState(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState('1h');
  const [activeTab, setActiveTab] = useState('overview'); // Overview tab'ı hemen aç
  const [eventsDisplayedCount, setEventsDisplayedCount] = useState(50);
  const [eventTypeFilter, setEventTypeFilter] = useState('all'); // all | critical | warning | info
  const [eventSourceFilter, setEventSourceFilter] = useState('all'); // all | gateway | sensor | system
  const [searchTerm, setSearchTerm] = useState('');
  
  // Konum verisi state'leri
  const [historicalLocationData, setHistoricalLocationData] = useState([]);
  const [isLoadingLocationData, setIsLoadingLocationData] = useState(false);
  
  // Pagination state'leri
  const [hasMoreData, setHasMoreData] = useState(false);
  const [lastTimestamp, setLastTimestamp] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedSensorMAC, setSelectedSensorMAC] = useState(null);
  
  // Firebase veri state'leri
  const [boatData, setBoatData] = useState({});
  const [availableBoats, setAvailableBoats] = useState([]);
  const [error, setError] = useState(null);
  const [firebaseStatus, setFirebaseStatus] = useState('checking');
  
  // User menu state'leri
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  
  // User menu ref for click outside
  const userMenuRef = useRef(null);

  // Yardımcı fonksiyonlar
  const parseCoordinate = useCallback((value) => {
    if (!value || value === '') return null;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  }, []);

  const parseAltitude = useCallback((value) => {
    if (!value || value === '') return null;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  }, []);

  const formatTimestamp = useCallback((timestamp) => {
    if (!timestamp) return 'N/A';
    // String ISO veya okunabilir tarih
    if (typeof timestamp === 'string') {
      const d = new Date(timestamp);
      return isNaN(d) ? 'N/A' : d.toLocaleString('tr-TR');
    }
    // Sayısal: saniye veya milisaniye olabilir
    if (typeof timestamp === 'number') {
      const ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
      const d = new Date(ms);
      return isNaN(d) ? 'N/A' : d.toLocaleString('tr-TR');
    }
    return 'N/A';
  }, []);

  // Click outside handler for user menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Firebase bağlantısını test et ve verileri yükle
  useEffect(() => {
    let isMounted = true;
    
    const loadFirebaseData = async () => {
      try {
        // ANINDA UI BOOTSTRAP - Her durumda dropdownlar dolu olsun
        setAvailableBoats([HARD_FALLBACK_IMEI]);
        setSelectedBoat(prev => prev || HARD_FALLBACK_IMEI);
        setFirebaseStatus('bootstrap');
        
        // 1) Local cache'den anında doldur (dropdown hemen açılsın)
        try {
          const cached = JSON.parse(localStorage.getItem('boatsCache') || '[]');
          if (Array.isArray(cached) && cached.length > 0) {
            setAvailableBoats(cached);
            if (!selectedBoat) {
              setSelectedBoat(cached[0]);
            }
            setFirebaseStatus('cache');
          }
        } catch {}
        
        // Tüm tekneleri al - timeout kaldırıldı, direkt await
        let boats = [];
        try {
          boats = await dataService.getBoatsList();
          console.log('🎯 DataService başarılı:', boats.length, 'tekne');
        } catch (err) {
          console.error('🔥 getBoatsList error:', err);
          boats = [];
        }
        
        if (!isMounted) return;
        
        console.log('⚡ Firebase OK:', boats.length, 'tekne');
        console.log('🔍 Gelen boats array:', boats);
        
        if (boats.length > 0) {
          const boatImeis = boats.map(boat => boat.imei);
          
          setAvailableBoats(boatImeis);
          setSelectedBoat(boatImeis[0]); // İlk tekneyi seç
          setFirebaseStatus('connected');
          // Cache'i güncelle
          try { localStorage.setItem('boatsCache', JSON.stringify(boatImeis)); } catch {}
        } else {
          console.log('❌ Canlı tekne listesi alınamadı, fallback IMEI ile devam ediliyor');
          // 1) Hard fallback IMEI ile hemen çalıştır
          if (HARD_FALLBACK_IMEI) {
            setAvailableBoats([HARD_FALLBACK_IMEI]);
            setSelectedBoat(HARD_FALLBACK_IMEI);
            setFirebaseStatus('fallback');
            try { localStorage.setItem('boatsCache', JSON.stringify([HARD_FALLBACK_IMEI])); } catch {}
          } else {
            // 2) Cache dolu ise hata vermeyelim
            setFirebaseStatus(prev => (prev === 'cache' ? prev : 'error'));
          }
        }
      } catch (err) {
        if (!isMounted) return;
        
        console.error('Firebase bağlantı hatası:', err);
        setFirebaseStatus('error');
        setError(err.message);
      } finally {
        if (isMounted) {
        }
      }
    };

    loadFirebaseData();

    // Cleanup
    return () => {
      isMounted = false;
      dataService.cleanup();
    };
  }, []);

  // Seçili tekne değiştiğinde
  // Tekne seçilince HEMEN veri dinlemeye başla (tab'dan bağımsız)
  useEffect(() => {
    if (!selectedBoat || firebaseStatus !== 'connected') {
      return;
    }

    // Aynı tekne için zaten listener varsa skip et
    if (boatData[selectedBoat] !== undefined) {
      return;
    }

    console.log('🎯 Tekne için veri dinleme başlatılıyor:', selectedBoat);

    // Önceki listener'ı temizle
      dataService.stopListening(selectedBoat);
    
    let timeoutId;
    let dataReceived = false;
    
    // Timeout ile veri yoksa boş göster
    timeoutId = setTimeout(() => {
      if (!dataReceived) {
        console.log('⏰ Firebase timeout - boş veri set ediliyor');
        setBoatData(prev => ({ ...prev, [selectedBoat]: null }));
      }
    }, 2000); // 2 saniye timeout (daha hızlı)
    
    // Veri dinlemeyi başlat - throttle ile
    let lastUpdate = 0;
      dataService.listenToBoatData(selectedBoat, (data) => {
      const now = Date.now();
      if (now - lastUpdate < 2000) return; // 2 saniye throttle (daha agresif)
      lastUpdate = now;
      
      dataReceived = true;
      clearTimeout(timeoutId);
      
        setBoatData(prev => ({ ...prev, [selectedBoat]: data }));
      });

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      dataService.stopListening(selectedBoat);
    };
  }, [selectedBoat, firebaseStatus]); // boatData dependency kaldırıldı

  // İlk 10 konum verisini hızlı yükle
  const loadInitialLocationData = useCallback(async () => {
    if (!selectedBoat) return;
    
    try {
      setIsLoadingLocationData(true);
      console.log('⚡ İlk 10 konum verisi yükleniyor...');
      
      const endTime = Date.now();
      let startTime;
      
      switch (selectedTimeRange) {
        case '1h': startTime = endTime - (60 * 60 * 1000); break;
        case '6h': startTime = endTime - (6 * 60 * 60 * 1000); break;
        case '24h': startTime = endTime - (24 * 60 * 60 * 1000); break;
        case '7d': startTime = endTime - (7 * 24 * 60 * 60 * 1000); break;
        case '30d': startTime = endTime - (30 * 24 * 60 * 60 * 1000); break;
        default: startTime = endTime - (60 * 60 * 1000);
      }
      
      // Pagination API kullan
      const result = await dataService.getPaginatedLocationData(selectedBoat, startTime, endTime, 10);
      
      const locationData = result.data
        .map(entry => {
          const gnss = entry.gateway_data?.gnss;
          if (!gnss) return null;
          
          const latitude = parseCoordinate(gnss.latitude || gnss.lat);
          const longitude = parseCoordinate(gnss.longitude || gnss.lng || gnss.lon);
          const altitude = parseAltitude(gnss.altitude || gnss.alt);
          const timestamp = entry.timestamp || gnss.timestamp || gnss.time || gnss.last_updated;
          
          if (latitude === null || longitude === null) return null;
          
          const timestampMs = timestamp ? (timestamp < 1e12 ? timestamp * 1000 : timestamp) : null;
          
          return {
            id: entry.id,
            latitude,
            longitude,
            altitude,
            timestamp,
            timestampMs,
            originalData: entry
          };
        })
        .filter(Boolean);
      
      console.log('⚡ İlk 10 konum verisi yüklendi:', locationData.length, 'nokta');
      setHistoricalLocationData(locationData);
      setHasMoreData(result.hasMore);
      setLastTimestamp(result.lastTimestamp);
      
    } catch (error) {
      console.error('❌ İlk konum verileri yüklenirken hata:', error);
      setHistoricalLocationData([]);
      setHasMoreData(false);
    } finally {
      setIsLoadingLocationData(false);
    }
  }, [selectedBoat, selectedTimeRange, parseCoordinate, parseAltitude]);

  // Daha fazla konum verisi yükle (10'ar 10'ar)
  const loadMoreLocationData = useCallback(async () => {
    if (!selectedBoat || !hasMoreData || isLoadingMore) return;
    
    try {
      setIsLoadingMore(true);
      console.log('📄 Sonraki 10 konum verisi yükleniyor...');
      
      const endTime = Date.now();
      let startTime;
      
      switch (selectedTimeRange) {
        case '1h': startTime = endTime - (60 * 60 * 1000); break;
        case '6h': startTime = endTime - (6 * 60 * 60 * 1000); break;
        case '24h': startTime = endTime - (24 * 60 * 60 * 1000); break;
        case '7d': startTime = endTime - (7 * 24 * 60 * 60 * 1000); break;
        case '30d': startTime = endTime - (30 * 24 * 60 * 60 * 1000); break;
        default: startTime = endTime - (60 * 60 * 1000);
      }
      
      const result = await dataService.getPaginatedLocationData(selectedBoat, startTime, endTime, 10, lastTimestamp);
      
      const newLocationData = result.data
        .map(entry => {
          const gnss = entry.gateway_data?.gnss;
          if (!gnss) return null;
          
          const latitude = parseCoordinate(gnss.latitude || gnss.lat);
          const longitude = parseCoordinate(gnss.longitude || gnss.lng || gnss.lon);
          const altitude = parseAltitude(gnss.altitude || gnss.alt);
          const timestamp = entry.timestamp || gnss.timestamp || gnss.time || gnss.last_updated;
          
          if (latitude === null || longitude === null) return null;
          
          const timestampMs = timestamp ? (timestamp < 1e12 ? timestamp * 1000 : timestamp) : null;
          
          return {
            id: entry.id,
            latitude,
            longitude,
            altitude,
            timestamp,
            timestampMs,
            originalData: entry
          };
        })
        .filter(Boolean);
      
      console.log('📄 Sonraki 10 konum verisi yüklendi:', newLocationData.length, 'nokta');
      setHistoricalLocationData(prev => [...prev, ...newLocationData]);
      setHasMoreData(result.hasMore);
      setLastTimestamp(result.lastTimestamp);
      
    } catch (error) {
      console.error('❌ Daha fazla konum verisi yüklenirken hata:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [selectedBoat, selectedTimeRange, hasMoreData, isLoadingMore, lastTimestamp, parseCoordinate, parseAltitude]);

  // Filtrelenmiş tekneler (memoized)
  const filteredBoats = useMemo(() => {
    return availableBoats.filter(imei => 
    imei.toLowerCase().includes(searchTerm.toLowerCase())
  );
  }, [availableBoats, searchTerm]);

  // Mevcut tekne verisi (memoized)
  const currentBoat = useMemo(() => {
    const boat = boatData[selectedBoat] || null;
    return boat;
  }, [boatData, selectedBoat]);

  // Sensor online kontrolü
  const isSensorOnline = useCallback((lastSeen) => {
    if (!lastSeen) return false;
    const lastSeenTime = new Date(lastSeen).getTime();
    const currentTime = Date.now();
    const timeDifference = currentTime - lastSeenTime;
    const fifteenMinutes = 15 * 60 * 1000; // 15 dakika
    return timeDifference <= fifteenMinutes;
  }, []);

  // Sensörleri lazy memoize et - sadece sensors tab'da hesapla
  const memoizedSensors = useMemo(() => {
    if (activeTab !== 'sensors' || !currentBoat?.sensors) return [];
    return Object.entries(currentBoat?.sensors || {}).map(([mac, sensor]) => ({
      mac,
      ...sensor,
      isOnline: isSensorOnline(sensor.last_seen)
    }));
  }, [activeTab, currentBoat?.sensors, isSensorOnline]);

  // Dropdown options
  const boatOptions = useMemo(() => 
    availableBoats.map(imei => ({
      value: imei,
      label: imei // Sadece IMEI görünsün, "Tekne #" kaldırıldı
    }))
  , [availableBoats]);

  const timeRangeOptions = [
    { value: '1h', label: 'Son 1 Saat' },
    { value: '6h', label: 'Son 6 Saat' },
    { value: '24h', label: 'Son 24 Saat' },
    { value: '7d', label: 'Son 7 Gün' },
    { value: '30d', label: 'Son 30 Gün' }
  ];

  // Location sekmesinde gerçek backend pagination
  useEffect(() => {
    if (activeTab === 'location' && selectedBoat) {
      // State'leri sıfırla
      setHistoricalLocationData([]);
      setHasMoreData(false);
      setLastTimestamp(null);
      
      // 🔧 GERÇEK PAGINATION: İlk 10 kayıt yükle
      console.log('🔧 Gerçek pagination - ilk 10 kayıt...');
      loadInitialLocationData();
    }
  }, [activeTab, selectedBoat, selectedTimeRange, loadInitialLocationData]);

  // Error state (hook-lardan sonra olmalı)

  // Yardımcı fonksiyonlar
  const getSignalQuality = (rsrp) => {
    if (rsrp > -85) return { quality: 'Mükemmel', color: 'text-green-600', bg: 'bg-green-100' };
    if (rsrp > -95) return { quality: 'İyi', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    if (rsrp > -105) return { quality: 'Orta', color: 'text-orange-600', bg: 'bg-orange-100' };
    return { quality: 'Zayıf', color: 'text-red-600', bg: 'bg-red-100' };
  };

  const getBatteryStatus = (percentage) => {
    const battery = parseInt(percentage);
    if (battery > 50) return { status: 'İyi', color: 'text-green-600', bg: 'bg-green-100' };
    if (battery > 20) return { status: 'Orta', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    return { status: 'Düşük', color: 'text-red-600', bg: 'bg-red-100' };
  };

  // Online/Offline durumu hesaplama fonksiyonları
  const isGatewayOnline = (lastUpdated) => {
    if (!lastUpdated) return false;
    const lastUpdateTime = new Date(lastUpdated).getTime();
    const currentTime = Date.now();
    const timeDifference = currentTime - lastUpdateTime;
    const fifteenMinutes = 15 * 60 * 1000; // 15 dakika
    return timeDifference <= fifteenMinutes;
  };

  // Online/Offline sayılarını hesapla
  const getOnlineOfflineCounts = () => {
    // Gateway durumu
    const gatewayLastUpdate = currentBoat?.gateway_data?.power_status?.last_updated;
    const gatewayOnline = isGatewayOnline(gatewayLastUpdate);
    const gatewayOnlineCount = gatewayOnline ? 1 : 0;
    const gatewayOfflineCount = gatewayOnline ? 0 : 1;

    // Sensör durumları
    const sensors = currentBoat?.sensors || {};
    let sensorOnlineCount = 0;
    let sensorOfflineCount = 0;

    Object.values(sensors).forEach(sensor => {
      if (isSensorOnline(sensor.last_seen)) {
        sensorOnlineCount++;
      } else {
        sensorOfflineCount++;
      }
    });

    return {
      gateway: { online: gatewayOnlineCount, offline: gatewayOfflineCount },
      sensors: { online: sensorOnlineCount, offline: sensorOfflineCount }
    };
  };

  const counts = getOnlineOfflineCounts();

  // Olaylar listesi - önemli durumların devreye alınması
  const criticalEvents = useMemo(() => {
    const events = [];
    const boat = currentBoat;
    
    // Eğer boat yoksa boş array döndür
    if (!boat) {
      return events;
    }

    const endTime = Date.now();
    const rangeMs = {
      '1h': 60 * 60 * 1000,
      '6h': 6 * 60 * 60 * 1000,
      '24h': 24 * 60 * 60 * 1000,
      '7d': 7 * 24 * 60 * 60 * 1000,
      '30d': 30 * 24 * 60 * 60 * 1000
    }[selectedTimeRange] || (60 * 60 * 1000);
    const startTime = endTime - rangeMs;

    const toMs = (value) => {
      if (!value) return null;
      if (typeof value === 'string') {
        const d = new Date(value);
        return isNaN(d) ? null : d.getTime();
      }
      if (typeof value === 'number') {
        return value < 1e12 ? value * 1000 : value;
      }
      return null;
    };
    const inRange = (value) => {
      const ms = toMs(value);
      return ms !== null && ms >= startTime && ms <= endTime;
    };

    // MQTT/Firebase durumları (basit varsayım: firebaseStatus !== connected)
    if (firebaseStatus !== 'connected') {
      events.push({
        type: 'firebase',
        label: 'Firebase bağlantısı yok',
        severity: 'critical',
        timestamp: endTime,
        source: 'Sistem'
      });
    }

    // Gateway offline
    const gatewayOnline = isGatewayOnline(boat?.gateway_data?.power_status?.last_updated);
    if (!gatewayOnline && inRange(boat?.gateway_data?.power_status?.last_updated || boat?.gateway_data?.power_status?.timestamp)) {
      events.push({
        type: 'gateway_offline',
        label: 'Gateway offline',
        severity: 'critical',
        timestamp: toMs(boat?.gateway_data?.power_status?.last_updated) || endTime,
        source: 'Gateway'
      });
    }

    // Düşük pil (gateway)
    const gwBatt = boat?.gateway_data?.power_status?.battery_percentage;
    const gwBattNum = typeof gwBatt === 'string' ? parseInt(gwBatt) : (typeof gwBatt === 'number' ? gwBatt : null);
    if (gwBattNum != null && gwBattNum < 20 && inRange(boat?.gateway_data?.power_status?.last_updated || boat?.gateway_data?.power_status?.timestamp)) {
      events.push({
        type: 'low_battery_gateway',
        label: `Gateway düşük pil: ${gwBattNum}%`,
        severity: 'warning',
        timestamp: toMs(boat?.gateway_data?.power_status?.last_updated) || endTime,
        source: 'Gateway'
      });
    }

    // Sensörler
    const sensors = boat?.sensors || {};
    Object.entries(sensors).forEach(([mac, sensor]) => {
      const model = (sensor.model || '').toUpperCase();
      const isOnline = isSensorOnline(sensor.last_seen);
      if (!isOnline && inRange(sensor.last_seen)) {
        events.push({
          type: 'sensor_offline',
          label: `Sensör offline (${sensor.model} - ${mac})`,
          severity: 'critical',
          timestamp: toMs(sensor.last_seen) || endTime,
          source: mac
        });
      }
      // Düşük pil (sensör)
      let batteryLevel = null;
      if (typeof sensor.battery === 'number') batteryLevel = sensor.battery;
      else if (typeof sensor.battery === 'object' && sensor.battery?.battery_level) batteryLevel = sensor.battery.battery_level;
      if (batteryLevel != null && batteryLevel < 20 && inRange(sensor.last_seen)) {
        events.push({
          type: 'low_battery_sensor',
          label: `Düşük pil (${sensor.model} - ${mac}): ${batteryLevel}%`,
          severity: 'warning',
          timestamp: toMs(sensor.last_seen) || endTime,
          source: mac
        });
      }

      // Su sızıntısı (MSL01)
      if (model.includes('MSL01')) {
        const wl = sensor.history?.waterleak;
        if (wl) {
          Object.entries(wl).forEach(([ts, data]) => {
            const keyMs = toMs(parseInt(ts));
            if (keyMs !== null && keyMs >= startTime && keyMs <= endTime && data?.water_leak === true) {
              events.push({
                type: 'water_leak',
                label: `Su sızıntısı tespit edildi (${mac})`,
                severity: 'critical',
                timestamp: keyMs,
                source: mac
              });
            }
          });
        }
      }

      // PIR hareket (MSP01)
      if (model.includes('MSP01')) {
        const pm = sensor.history?.['PIR-motion'];
        if (pm) {
          Object.entries(pm).forEach(([ts, data]) => {
            const keyMs = toMs(parseInt(ts));
            const pir = data?.motion ?? data?.PIR_motion ?? data?.PIR ?? data?.motion_detected;
            if (keyMs !== null && keyMs >= startTime && keyMs <= endTime && pir === true) {
              events.push({
                type: 'pir_motion',
                label: `PIR hareket algılandı (${mac})`,
                severity: 'info',
                timestamp: keyMs,
                source: mac
              });
            }
          });
        }
      }

      // Door tamper (S4)
      if (model.includes('S4')) {
        const ds = sensor.history?.doorstatus;
        if (ds) {
          Object.entries(ds).forEach(([ts, data]) => {
            const keyMs = toMs(parseInt(ts));
            if (keyMs !== null && keyMs >= startTime && keyMs <= endTime && data?.tamper_triggered === true) {
              events.push({
                type: 'door_tamper',
                label: `Kapı tamper algılandı (${mac})`,
                severity: 'warning',
                timestamp: keyMs,
                source: mac
              });
            }
          });
        }
      }
    });

    // MQTT kopması - elimizde gerçek sinyal yok; placeholder (ileride bağlanabilir)
    // events.push({ type: 'mqtt_disconnected', label: 'MQTT bağlantısı kesildi', severity: 'critical', timestamp: Date.now(), source: 'Sistem' });

    // Zamana göre yeni → eski sırala
    return events.sort((a, b) => b.timestamp - a.timestamp);
  }, [currentBoat, firebaseStatus, isSensorOnline, selectedTimeRange]);

  // Sıcaklık verisini işleme fonksiyonu
  const getTemperatureValue = (temperatureData) => {
    if (!temperatureData) return 'N/A';
    
    // Eğer direkt sayı ise
    if (typeof temperatureData === 'number') {
      return temperatureData;
    }
    
    // Eğer obje ise ve value property'si varsa
    if (typeof temperatureData === 'object' && temperatureData.value !== undefined) {
      return temperatureData.value;
    }
    
    // Eğer obje ise ama value yoksa, ilk sayısal değeri bul
    if (typeof temperatureData === 'object') {
      const values = Object.values(temperatureData);
      const numericValue = values.find(val => typeof val === 'number');
      if (numericValue !== undefined) {
        return numericValue;
      }
    }
    
    return 'N/A';
  };

  const signalStatus = getSignalQuality(currentBoat?.gateway_data?.cellular_status?.rsrp || -95);
  const batteryStatus = getBatteryStatus(currentBoat?.gateway_data?.power_status?.battery_percentage || '0%');

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <div className="flex items-center space-x-4 mb-2">
                <Logo size="large" />
              </div>
              <p className="text-sm text-gray-600">
                IoT Tekne İzleme Sistemi
              </p>
            </div>
            
            {/* Middle Section - IMEI Search and Dropdown */}
            <div className="flex items-center space-x-4 flex-1 max-w-2xl mx-4">
              {/* IMEI Search */}
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center justify-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="IMEI ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {searchTerm && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                    {filteredBoats.map((imei) => (
                      <button
                        key={imei}
                        onClick={() => {
                          setSelectedBoat(imei);
                          setSearchTerm('');
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                      >
                        <div className="font-medium text-gray-900">Tekne #{imei}</div>
                        <div className="text-sm text-gray-500">
                          Batarya: {boatData[imei]?.gateway_data?.power_status?.battery_percentage || 'N/A'}
                        </div>
                      </button>
                    ))}
                    {filteredBoats.length === 0 && (
                      <div className="px-4 py-2 text-gray-500">Sonuç bulunamadı</div>
                    )}
                  </div>
                )}
              </div>
              
              <CustomDropdown
                value={selectedBoat || ''} 
                onChange={(value) => setSelectedBoat(value)}
                options={boatOptions}
                placeholder="IMEI Seçin"
                disabled={availableBoats.length === 0}
                className="min-w-48"
              />
            </div>
            
            {/* Right Section - User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 px-3 py-2 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition duration-200 shadow-sm"
                title="Kullanıcı Menüsü"
              >
                <User className="h-4 w-4 text-gray-600" />
                <span className="text-sm text-gray-700 hidden sm:inline">
                  {localStorage.getItem('currentUser') || 'Kullanıcı'}
                </span>
                <ChevronDown className={`h-4 w-4 text-gray-600 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {/* User Menu Dropdown */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setShowPasswordModal(true);
                        setShowUserMenu(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition duration-150"
                    >
                      <Settings className="h-4 w-4 mr-3" />
                      Şifre Değiştir
                    </button>
                    <hr className="my-1" />
                    <button
                      onClick={onLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition duration-150"
                    >
                      <LogOut className="h-4 w-4 mr-3" />
                      Çıkış Yap
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {['overview', 'gateway', 'sensors', 'location', 'events'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab === 'overview' && 'Genel Bakış'}
                  {tab === 'gateway' && 'Gateway'}
                  {tab === 'sensors' && 'Sensörler'}
                  {tab === 'location' && 'Konum'}
                  {tab === 'events' && 'Olaylar'}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Status Cards - Sadece Genel Bakış sekmesinde görünür */}
        {activeTab === 'overview' && !currentBoat && selectedBoat && (
          <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50">
            <div className="text-center">
              <div className="mb-6">
                <Logo size="large" />
              </div>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
              <p className="text-gray-600">Veriler yükleniyor...</p>
            </div>
          </div>
        )}
        
        {activeTab === 'overview' && currentBoat && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Firebase Bağlantı</p>
                  <p className={`text-2xl font-bold ${firebaseStatus === 'connected' ? 'text-green-600' : 'text-red-600'}`}>
                    {firebaseStatus === 'connected' ? 'Bağlı' : 'Bağlantı Yok'}
                  </p>
                </div>
                <div className={`p-3 ${firebaseStatus === 'connected' ? 'bg-green-100' : 'bg-red-100'} rounded-full`}>
                  {firebaseStatus === 'connected' ? (
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  ) : (
                    <XCircle className="h-6 w-6 text-red-600" />
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Realtime Database
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">MQTT Broker</p>
                  <p className="text-2xl font-bold text-green-600">Online</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <Wifi className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                mqtt.axutech.com:1883
              </p>
            </div>

                         <div className="bg-white rounded-lg shadow-sm p-6">
               <div className="flex items-center justify-between">
                 <div>
                   <p className="text-sm font-medium text-gray-600">Gateway Durumu</p>
                   <p className={`text-2xl font-bold ${counts.gateway.online > 0 ? 'text-green-600' : 'text-red-600'}`}>
                     {counts.gateway.online} Online
                   </p>
                 </div>
                 <div className={`p-3 ${counts.gateway.online > 0 ? 'bg-green-100' : 'bg-red-100'} rounded-full`}>
                   <Activity className={`h-6 w-6 ${counts.gateway.online > 0 ? 'text-green-600' : 'text-red-600'}`} />
                 </div>
               </div>
               <p className="text-xs text-gray-500 mt-2">
                 {counts.gateway.offline} Offline
               </p>
             </div>

             <div className="bg-white rounded-lg shadow-sm p-6">
               <div className="flex items-center justify-between">
                 <div>
                   <p className="text-sm font-medium text-gray-600">Sensör Durumu</p>
                   <p className={`text-2xl font-bold ${counts.sensors.online > 0 ? 'text-green-600' : 'text-red-600'}`}>
                     {counts.sensors.online} Online
                   </p>
                 </div>
                 <div className={`p-3 ${counts.sensors.online > 0 ? 'bg-green-100' : 'bg-red-100'} rounded-full`}>
                   <Activity className={`h-6 w-6 ${counts.sensors.online > 0 ? 'text-green-600' : 'text-red-600'}`} />
                 </div>
               </div>
               <p className="text-xs text-gray-500 mt-2">
                 {counts.sensors.offline} Offline
               </p>
             </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Kritik Pil</p>
                  <p className="text-2xl font-bold text-red-600">0</p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <Battery className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Gateway & Sensör
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Sistem Sağlığı</p>
                  <p className="text-2xl font-bold text-green-600">%100</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Tüm sistemler aktif
              </p>
            </div>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Sistem Sağlığı</h3>
            <div className="space-y-4">
              <p className="text-gray-600">
                MarIoT Boat sisteminin genel sağlık durumu ve bağlantı bilgileri. 
                Tüm bileşenlerin durumu gerçek zamanlı olarak izleniyor.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-medium text-green-900 mb-2">Bağlantı Durumları</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-green-700">Firebase Database:</span>
                      <span className="text-sm font-medium text-green-600">
                        {firebaseStatus === 'connected' ? 'Bağlı' : 'Bağlantı Yok'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-green-700">MQTT Broker:</span>
                      <span className="text-sm font-medium text-green-600">Online</span>
                    </div>
                  </div>
                </div>
                
                                 <div className="bg-blue-50 rounded-lg p-4">
                   <h4 className="font-medium text-blue-900 mb-2">Cihaz Durumları</h4>
                   <div className="space-y-2">
                     <div className="flex items-center justify-between">
                       <span className="text-sm text-blue-700">Gateway:</span>
                       <span className={`text-sm font-medium ${counts.gateway.online > 0 ? 'text-green-600' : 'text-red-600'}`}>
                         {counts.gateway.online} Online / {counts.gateway.offline} Offline
                       </span>
                     </div>
                     <div className="flex items-center justify-between">
                       <span className="text-sm text-blue-700">Sensörler:</span>
                       <span className={`text-sm font-medium ${counts.sensors.online > 0 ? 'text-green-600' : 'text-red-600'}`}>
                         {counts.sensors.online} Online / {counts.sensors.offline} Offline
                       </span>
                     </div>
                   </div>
                 </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'gateway' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Gateway Durumu</h3>
            <div className="space-y-6">
              {/* Gateway Genel Bilgileri */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                 <div className="bg-gray-50 rounded-lg p-4">
                   <div className="flex items-center justify-between">
                     <div>
                       <p className="text-sm font-medium text-gray-600">Gateway Durumu</p>
                       <p className={`text-2xl font-bold ${counts.gateway.online > 0 ? 'text-green-600' : 'text-red-600'}`}>
                         {counts.gateway.online > 0 ? 'Aktif' : 'Offline'}
                       </p>
                     </div>
                     <div className={`p-3 ${counts.gateway.online > 0 ? 'bg-green-100' : 'bg-red-100'} rounded-full`}>
                       {counts.gateway.online > 0 ? (
                         <CheckCircle className="h-6 w-6 text-green-600" />
                       ) : (
                         <XCircle className="h-6 w-6 text-red-600" />
                       )}
                     </div>
                   </div>
                                       <p className="text-xs text-gray-500 mt-2">
                      Son güncelleme: {formatTimestamp(currentBoat?.gateway_data?.power_status?.last_updated)}
                    </p>
                 </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Batarya Seviyesi</p>
                      <p className={`text-2xl font-bold ${batteryStatus.color}`}>
                        {currentBoat?.gateway_data?.power_status?.battery_percentage || 'N/A'}
                      </p>
                    </div>
                    <div className={`p-3 ${batteryStatus.bg} rounded-full`}>
                      <Battery className={`h-6 w-6 ${batteryStatus.color}`} />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Gerilim: {currentBoat?.gateway_data?.power_status?.supply_voltage || 'N/A'}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Sinyal Kalitesi</p>
                      <p className={`text-2xl font-bold ${signalStatus.color}`}>
                        {signalStatus.quality}
                      </p>
                    </div>
                    <div className={`p-3 ${signalStatus.bg} rounded-full`}>
                      <Signal className={`h-6 w-6 ${signalStatus.color}`} />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    RSRP: {currentBoat?.gateway_data?.cellular_status?.rsrp || 'N/A'} dBm
                  </p>
                </div>

                                 <div className="bg-gray-50 rounded-lg p-4">
                   <div className="flex items-center justify-between">
                     <div>
                       <p className="text-sm font-medium text-gray-600">Sıcaklık</p>
                       <p className="text-2xl font-bold text-blue-600">
                         {getTemperatureValue(currentBoat?.gateway_data?.temperature)}°C
                       </p>
                     </div>
                     <div className="p-3 bg-blue-100 rounded-full">
                       <Thermometer className="h-6 w-6 text-blue-600" />
                     </div>
                   </div>
                   <p className="text-xs text-gray-500 mt-2">
                     Dahili sensör
                   </p>
                 </div>
              </div>

                             {/* Gateway Detay Bilgileri */}
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                 <div className="bg-gray-50 rounded-lg p-4">
                   <h4 className="font-medium text-gray-900 mb-3">Güç Durumu</h4>
                   <div className="space-y-2 text-sm">
                     <div className="flex justify-between">
                       <span>Batarya Yüzdesi:</span>
                       <span className="font-medium">{currentBoat?.gateway_data?.power_status?.battery_percentage || 'N/A'}</span>
                     </div>
                     <div className="flex justify-between">
                       <span>Tedarik Gerilimi:</span>
                       <span className="font-medium">{currentBoat?.gateway_data?.power_status?.supply_voltage || 'N/A'}</span>
                     </div>
                     <div className="flex justify-between">
                       <span>Enerji Kaynağı:</span>
                       <span className="font-medium">{currentBoat?.gateway_data?.power_status?.external_power_supply ? 'Harici Enerji' : 'Batarya'}</span>
                     </div>
                   </div>
                 </div>

                 <div className="bg-gray-50 rounded-lg p-4">
                   <h4 className="font-medium text-gray-900 mb-3">Hücresel Bağlantı</h4>
                   <div className="space-y-2 text-sm">
                     <div className="flex justify-between">
                       <span>RSRP:</span>
                       <span className="font-medium">{currentBoat?.gateway_data?.cellular_status?.rsrp || 'N/A'} dBm</span>
                     </div>
                     <div className="flex justify-between">
                       <span>Network Format:</span>
                       <span className="font-medium">{currentBoat?.gateway_data?.cellular_status?.network_format || 'N/A'}</span>
                     </div>
                     <div className="flex justify-between">
                       <span>Time Stamp:</span>
                       <span className="font-medium">{formatTimestamp(currentBoat?.gateway_data?.cellular_status?.timestamp)}</span>
                     </div>
                   </div>
                 </div>

                 <div className="bg-gray-50 rounded-lg p-4">
                   <h4 className="font-medium text-gray-900 mb-3">İvmeölçer</h4>
                   <div className="space-y-2 text-sm">
                     <div className="flex justify-between">
                       <span>X Ekseni:</span>
                       <span className="font-medium">{currentBoat?.gateway_data?.acceleration?.x_axis || 'N/A'}</span>
                     </div>
                     <div className="flex justify-between">
                       <span>Y Ekseni:</span>
                       <span className="font-medium">{currentBoat?.gateway_data?.acceleration?.y_axis || 'N/A'}</span>
                     </div>
                     <div className="flex justify-between">
                       <span>Z Ekseni:</span>
                       <span className="font-medium">{currentBoat?.gateway_data?.acceleration?.z_axis || 'N/A'}</span>
                     </div>
                     <div className="flex justify-between">
                       <span>Zaman:</span>
                       <span className="font-medium">{formatTimestamp(currentBoat?.gateway_data?.acceleration?.timestamp)}</span>
                     </div>
                   </div>
                 </div>

                                   <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Digital Inputs</h4>
                                         <div className="space-y-2 text-sm">
                       {(() => {
                         // Firebase'deki binary_state verilerini kontrol et
                         const binaryData = currentBoat?.gateway_data?.binary_state;
                         
                         if (binaryData && typeof binaryData === 'object') {
                           const entries = Object.entries(binaryData).filter(([key, value]) => 
                             key !== 'timestamp' && typeof value === 'boolean'
                           );
                           
                           if (entries.length > 0) {
                             return entries.map(([key, value]) => {
                               // Türkçe açıklamalar
                               const descriptions = {
                                 'signal_cable_trigger': 'Sinyal Kablosu Tetikleyici',
                                 'device_reset': 'Cihaz Sıfırlama',
                                 'tap_s': 'Tek Dokunuş (Kısa)',
                                 'tap_d': 'Çift Dokunuş',
                                 'tap_l': 'Uzun Dokunuş'
                               };
                               
                               const description = descriptions[key] || key.replace(/_/g, ' ');
                               
                               return (
                                 <div key={key} className="flex justify-between items-center">
                                   <span className="text-gray-700">{description}:</span>
                                   <div className="flex items-center space-x-2">
                                     <span className={`font-medium ${value ? 'text-green-600' : 'text-red-600'}`}>
                                       {value ? 'Aktif' : 'Pasif'}
                                     </span>
                                     <div className={`w-2 h-2 rounded-full ${value ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                   </div>
                                 </div>
                               );
                             });
                           }
                         }
                         
                         return <div className="text-gray-500">Digital input verisi yok</div>;
                       })()}
                     </div>
                                         {(() => {
                       const binaryData = currentBoat?.gateway_data?.binary_state;
                       
                       if (binaryData?.timestamp) {
                         return (
                           <div className="mt-3 pt-2 border-t border-gray-200">
                             <div className="text-xs text-gray-500">
                               Son güncelleme: {formatTimestamp(binaryData.timestamp)}
                             </div>
                           </div>
                         );
                       }
                       return null;
                     })()}
                  </div>
               </div>
            </div>
          </div>
        )}

        {activeTab === 'sensors' && (
           <div className="space-y-6">
             {/* Sensor Status Table */}
           <div className="bg-white rounded-lg shadow-sm p-6">
               <h3 className="text-lg font-semibold text-gray-900 mb-4">
                 Sensör Durumları ve Verileri
                 <span className="text-sm font-normal text-gray-500 ml-2">(Detaylar için satıra tıklayın)</span>
               </h3>
               <div className="overflow-x-auto">
                 <table className="min-w-full divide-y divide-gray-200">
                   <thead className="bg-gray-50">
                     <tr>
                       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                         Sensör Tipi
                       </th>
                       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                         Model/MAC
                       </th>
                       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                         Batarya
                       </th>
                       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                         Sinyal (RSSI)
                       </th>
                       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                         Son Veriler
                       </th>
                       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                         Durum
                       </th>
                     </tr>
                   </thead>
                   <tbody className="bg-white divide-y divide-gray-200">
                              {memoizedSensors.map((sensor) => {
                       // Minew sensör tipi belirleme - Kullanıcı tanımlamasına göre
                       const getSensorTypeName = (model) => {
                         if (!model) return 'Bilinmeyen Sensör';
                         const modelUpper = model.toUpperCase();
                         
                         if (modelUpper.includes('MG5')) {
                           return '🌐 MG5 Mobile Gateway';
                         }
                         if (modelUpper.includes('MSP01')) {
                           return '👁️ MSP01 PIR Motion Sensör';
                         }
                         if (modelUpper.includes('MST01')) {
                           return '🌡️ MST01 Sıcaklık Sensörü';
                         }
                         if (modelUpper.includes('MSL01')) {
                           return '💧 MSL01 Su Kaçağı Sensörü';
                         }
                         if (modelUpper.includes('S4')) {
                           return '🚪 S4 Kapı Sensörü';
                         }
                         
                         return `📡 ${model}`;
                       };

                 return (
                         <tr 
                    key={sensor.mac} 
                           className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => setSelectedSensorMAC(sensor.mac)}
                  >
                                                       <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className={`w-2 h-2 rounded-full mr-2 ${sensor.isOnline ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{getSensorTypeName(sensor.model)}</div>
                        </div>
                        </div>
                            </td>
                           <td className="px-6 py-4 whitespace-nowrap">
                             <div className="text-sm text-gray-900">{sensor.model}</div>
                             <div className="text-xs text-gray-500">{sensor.mac}</div>
                           </td>
                           <td className="px-6 py-4 whitespace-nowrap">
                             {(() => {
                               // Batarya verisini güvenli şekilde parse et
                               let batteryValue = 'N/A';
                               let batteryLevel = null;
                               
                               if (typeof sensor.battery === 'number') {
                                 batteryValue = `${sensor.battery}%`;
                                 batteryLevel = sensor.battery;
                               } else if (typeof sensor.battery === 'object' && sensor.battery !== null) {
                                 // Obje formatında geldiğinde battery_level'ı kullan
                                 if (typeof sensor.battery.battery_level === 'number') {
                                   batteryValue = `${sensor.battery.battery_level}%`;
                                   batteryLevel = sensor.battery.battery_level;
                                 } else if (typeof sensor.battery.level === 'number') {
                                   batteryValue = `${sensor.battery.level}%`;
                                   batteryLevel = sensor.battery.level;
                                 } else {
                                   batteryValue = 'N/A';
                                 }
                               } else if (typeof sensor.battery === 'string') {
                                 batteryValue = sensor.battery;
                               }
                               
                               return (
                                 <>
                                   <div className={`text-sm font-medium ${
                                     batteryLevel !== null
                                       ? batteryLevel < 20 ? 'text-red-600' 
                                         : batteryLevel < 30 ? 'text-yellow-600' 
                                         : 'text-green-600'
                                       : 'text-gray-500'
                                   }`}>
                                     {batteryValue}
                          </div>
                                   {batteryLevel !== null && batteryLevel < 30 && (
                                     <div className="flex items-center mt-1">
                                       <AlertTriangle className="h-3 w-3 text-yellow-500 mr-1" />
                                       <span className="text-xs text-yellow-600">Düşük</span>
                        </div>
                                   )}
                                 </>
                               );
                             })()}
                           </td>
                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                             <div>{typeof sensor.rssi === 'number' ? sensor.rssi : 'N/A'} dBm</div>
                             <div className="text-xs text-gray-400">
                               {sensor.last_seen ? new Date(sensor.last_seen).toLocaleTimeString('tr-TR') : 'N/A'}
                          </div>
                           </td>
                           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                             <div className="space-y-1">
                               {/* Sensör tipine göre uygun verileri göster */}
                      {(() => {
                                 const measurements = sensor.measurements || {};
                                 const binaryStates = sensor.binary_states || {};
                                 const model = sensor.model?.toUpperCase() || '';
                                 const dataPoints = [];

                                 // Gerçek sensör verilerini history'den al (Firebase yapısına göre)
                                 const getLatestSensorData = (sensor, dataType) => {
                                   if (!sensor.history || !sensor.history[dataType]) return null;
                                   
                                   const historyEntries = Object.entries(sensor.history[dataType]);
                                   if (historyEntries.length === 0) return null;
                                   
                                   // En son timestamp'e göre sırala ve en son veriyi al
                                   const sortedEntries = historyEntries.sort(([a], [b]) => parseInt(b) - parseInt(a));
                                   return sortedEntries[0][1]; // En son veri
                                 };
                                 
                                                                 // Debug logs removed to prevent performance issues
                                 
                                 // Gerçek Firebase veri yapısına göre sensörleri parse et
                                 if (model.includes('MSL01')) {
                                   // MSL01 Water Leakage Sensor - Önce history'den su kaçağı verisi
                                   const waterData = getLatestSensorData(sensor, 'waterleak');
                                   if (waterData) {
                                     dataPoints.push(
                                       <div key="water" className={`text-xs ${waterData.water_leak ? 'text-red-600' : 'text-green-600'}`}>
                                         Su Kaçağı: {waterData.water_leak ? 'VAR!' : 'YOK'}
                                       </div>
                                     );
                                   }
                                   
                                   // Batarya verisi kaldırıldı - Son Veriler kısmında gösterilmeyecek
                                 }
                                 
                                 else if (model.includes('MSP01')) {
                                   // MSP01 PIR Sensor - Önce direkt sensör objesinden güncel veriler
                                   
                                   // Sıcaklık ve nem verisi (direkt sensör objesi)
                                   if (sensor.temperature) {
                                     if (sensor.temperature.temperature !== undefined) {
                                       dataPoints.push(
                                         <div key="temp" className="text-xs text-blue-600">
                                           Sıcaklık: {sensor.temperature.temperature.toFixed(1)}°C
                                         </div>
                                       );
                                     }
                                     if (sensor.temperature.humidity !== undefined) {
                                       dataPoints.push(
                                         <div key="humidity" className="text-xs text-teal-600">
                                           Nem: {sensor.temperature.humidity.toFixed(1)}%
                                         </div>
                                       );
                                     }
                                   }
                                   
                                   // Işık verisi (direkt sensör objesi)
                                   if (sensor.illuminance && sensor.illuminance.illuminance !== undefined) {
                                     dataPoints.push(
                                       <div key="light" className="text-xs text-yellow-600">
                                         Işık: {sensor.illuminance.illuminance.toFixed(0)} lux
                                       </div>
                                     );
                                   }
                                   
                                   // Batarya verisi kaldırıldı - Son Veriler kısmında gösterilmeyecek
                                   
                                   // History'den hareket ve ivme verisi (eğer varsa)
                                   const pirMotionData = getLatestSensorData(sensor, 'PIR-motion');
                                   const accelerationData = getLatestSensorData(sensor, 'acceleration');
                                   
                                   // PIR Motion verisi
                                   if (pirMotionData && pirMotionData.motion_detected !== undefined) {
                                     dataPoints.push(
                                       <div key="motion" className={`text-xs ${pirMotionData.motion_detected ? 'text-red-600' : 'text-gray-500'}`}>
                                         Hareket: {pirMotionData.motion_detected ? 'ALGILANDI' : 'YOK'}
                                       </div>
                                     );
                                   }
                                   
                                   // İvmeölçer verisi
                                   if (accelerationData) {
                                     if (accelerationData.x !== undefined || accelerationData.y !== undefined || accelerationData.z !== undefined) {
                                       const totalAccel = Math.sqrt(
                                         Math.pow(accelerationData.x || 0, 2) + 
                                         Math.pow(accelerationData.y || 0, 2) + 
                                         Math.pow(accelerationData.z || 0, 2)
                                       );
                                       dataPoints.push(
                                         <div key="accel" className="text-xs text-purple-600">
                                           İvme: {totalAccel.toFixed(2)}g
                                         </div>
                                       );
                                     }
                                   }
                                 }
                                 
                                 else if (model.includes('MST01')) {
                                   // MST01 Temperature Sensor - Direkt sensör objesinden güncel veriler
                                   
                                   // Sıcaklık ve nem verisi (direkt sensör objesi)
                                   if (sensor.temperature) {
                                     if (sensor.temperature.temperature !== undefined) {
                                       dataPoints.push(
                                         <div key="temp" className="text-xs text-blue-600">
                                           Sıcaklık: {sensor.temperature.temperature.toFixed(1)}°C
                                         </div>
                                       );
                                     }
                                     if (sensor.temperature.humidity !== undefined) {
                                       dataPoints.push(
                                         <div key="humidity" className="text-xs text-teal-600">
                                           Nem: {sensor.temperature.humidity.toFixed(1)}%
                                         </div>
                                       );
                                     }
                                   }
                                   
                                   // Batarya verisi kaldırıldı - Son Veriler kısmında gösterilmeyecek
                                 }
                                 
                                 else if (model.includes('S4')) {
                                   // Minew S4 Door Sensor - Önce direkt sensör objesinden güncel veriler
                                   
                                   // Kapı durumu verisi (direkt sensör objesi)
                                   if (sensor.doorstatus) {
                                     if (sensor.doorstatus.door_open !== undefined) {
                                       dataPoints.push(
                                         <div key="door" className={`text-xs ${sensor.doorstatus.door_open ? 'text-red-600' : 'text-green-600'}`}>
                                           Kapı: {sensor.doorstatus.door_open ? 'AÇIK' : 'KAPALI'}
                                    </div>
                                       );
                                     }
                                     if (sensor.doorstatus.tamper_triggered !== undefined) {
                                       dataPoints.push(
                                         <div key="tamper" className={`text-xs ${sensor.doorstatus.tamper_triggered ? 'text-red-600' : 'text-green-600'}`}>
                                           Tamper: {sensor.doorstatus.tamper_triggered ? 'UYARI!' : 'GÜVENLİ'}
                                </div>
                                       );
                                     }
                                   }
                                   
                                   // Batarya verisi kaldırıldı - Son Veriler kısmında gösterilmeyecek
                                   
                                   // History'den kapı durumu verisi (eğer direkt objede yoksa)
                                   if (!sensor.doorstatus) {
                                     const doorStatusData = getLatestSensorData(sensor, 'doorstatus');
                                     if (doorStatusData) {
                                       if (doorStatusData.door_open !== undefined) {
                                         dataPoints.push(
                                           <div key="door" className={`text-xs ${doorStatusData.door_open ? 'text-red-600' : 'text-green-600'}`}>
                                             Kapı: {doorStatusData.door_open ? 'AÇIK' : 'KAPALI'}
                                           </div>
                                         );
                                       }
                                       if (doorStatusData.tamper_triggered !== undefined) {
                                         dataPoints.push(
                                           <div key="tamper" className={`text-xs ${doorStatusData.tamper_triggered ? 'text-red-600' : 'text-green-600'}`}>
                                             Tamper: {doorStatusData.tamper_triggered ? 'UYARI!' : 'GÜVENLİ'}
                                           </div>
                                         );
                                       }
                                     }
                                   }
                                   
                                   // Tamper durumu için ayrı history kontrolü (eğer direkt objede yoksa)
                                   if (!sensor.doorstatus || sensor.doorstatus.tamper_triggered === undefined) {
                                     const tamperData = getLatestSensorData(sensor, 'tamper');
                                     if (tamperData && tamperData.tamper_triggered !== undefined) {
                                       dataPoints.push(
                                         <div key="tamper-history" className={`text-xs ${tamperData.tamper_triggered ? 'text-red-600' : 'text-green-600'}`}>
                                           Tamper: {tamperData.tamper_triggered ? 'UYARI!' : 'GÜVENLİ'}
                                         </div>
                                       );
                                     }
                                   }
                                 }
                                 
                                 else if (model.includes('MG5')) {
                                   // MG5 Mobile Gateway - Direkt sensör objesinden güncel veriler
                                   
                                   // Sıcaklık ve nem verisi (direkt sensör objesi)
                                   if (sensor.temperature) {
                                     if (sensor.temperature.temperature !== undefined) {
                                       dataPoints.push(
                                         <div key="temp" className="text-xs text-blue-600">
                                           Sıcaklık: {sensor.temperature.temperature.toFixed(1)}°C
                                         </div>
                                       );
                                     }
                                     if (sensor.temperature.humidity !== undefined) {
                                       dataPoints.push(
                                         <div key="humidity" className="text-xs text-teal-600">
                                           Nem: {sensor.temperature.humidity.toFixed(1)}%
                                         </div>
                                       );
                                     }
                                   }
                                   
                                   // GNSS verisi (direkt sensör objesi)
                                   if (sensor.gnss) {
                                     if (sensor.gnss.latitude !== undefined && sensor.gnss.longitude !== undefined) {
                                       dataPoints.push(
                                         <div key="gnss" className="text-xs text-green-600">
                                           GNSS: {sensor.gnss.latitude.toFixed(6)}, {sensor.gnss.longitude.toFixed(6)}
                                         </div>
                                       );
                                     }
                                   }
                                   
                                   // Güç durumu verisi (direkt sensör objesi)
                                   if (sensor.power_status) {
                                     if (sensor.power_status.external_power_supply !== undefined) {
                                       dataPoints.push(
                                         <div key="power" className={`text-xs ${sensor.power_status.external_power_supply ? 'text-green-600' : 'text-yellow-600'}`}>
                                           Güç: {sensor.power_status.external_power_supply ? 'Harici' : 'Batarya'}
                                         </div>
                                       );
                                     }
                                   }
                                   
                                   // Batarya verisi kaldırıldı - Son Veriler kısmında gösterilmeyecek
                                   
                                   // History'den veri (eğer direkt objede yoksa)
                                   if (!sensor.temperature && !sensor.gnss && !sensor.power_status) {
                                     const gnssData = getLatestSensorData(sensor, 'gnss');
                                     const environmentalData = getLatestSensorData(sensor, 'environmental');
                                     const powerData = getLatestSensorData(sensor, 'power');
                                     
                                     if (environmentalData) {
                                       if (environmentalData.temperature !== undefined) {
                                         dataPoints.push(
                                           <div key="temp" className="text-xs text-blue-600">
                                             Sıcaklık: {environmentalData.temperature.toFixed(1)}°C
                                    </div>
                                  );
                                       }
                                       if (environmentalData.humidity !== undefined) {
                                         dataPoints.push(
                                           <div key="humidity" className="text-xs text-teal-600">
                                             Nem: {environmentalData.humidity.toFixed(1)}%
                                </div>
                                         );
                                       }
                                     }
                                     
                                     if (gnssData && gnssData.latitude !== undefined && gnssData.longitude !== undefined) {
                                       dataPoints.push(
                                         <div key="gnss" className="text-xs text-green-600">
                                           GNSS: {gnssData.latitude.toFixed(6)}, {gnssData.longitude.toFixed(6)}
                                         </div>
                                       );
                                     }
                                     
                                     if (powerData && powerData.external_power !== undefined) {
                                       dataPoints.push(
                                         <div key="power" className={`text-xs ${powerData.external_power ? 'text-green-600' : 'text-yellow-600'}`}>
                                           Güç: {powerData.external_power ? 'Harici' : 'Batarya'}
                          </div>
                        );
                                     }
                                   }
                                 }

                                 // Hiç veri yoksa - Hangi history tiplerinin mevcut olduğunu göster
                                 if (dataPoints.length === 0) {
                                   const availableHistoryTypes = sensor.history ? Object.keys(sensor.history) : [];
                                   
                                   if (availableHistoryTypes.length > 0) {
                                                                         // Debug logs removed to prevent performance issues
                                     
                                     dataPoints.push(
                                       <div key="no-data" className="text-xs text-orange-500">
                                         Veri tipi: {availableHistoryTypes.join(', ')}
                                  </div>
                                     );
                                   } else {
                                     dataPoints.push(
                                       <div key="no-data" className="text-xs text-gray-400">
                                         Henüz veri yok
                                </div>
                              );
                                   }
                                 }

                                 return dataPoints;
                               })()}
                          </div>
                           </td>
                           <td className="px-6 py-4 whitespace-nowrap">
                             <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                               sensor.rssi > -50 ? 'bg-green-100 text-green-800' :
                               sensor.rssi > -70 ? 'bg-yellow-100 text-yellow-800' : 
                               'bg-red-100 text-red-800'
                             }`}>
                               {sensor.isOnline ? 'Aktif' : 'Offline'}
                             </span>
                           </td>
                         </tr>
                 );
               })}
                   </tbody>
                 </table>
               </div>
             </div>
           </div>
         )}

        {/* Sensör Detay Modal */}
        {selectedSensorMAC && (
           <SensorDetail
             sensor={memoizedSensors.find(s => s.mac === selectedSensorMAC)}
             onBack={() => setSelectedSensorMAC(null)}
             boatId={selectedBoat}
            isModal={true}
           />
         )}

                 {activeTab === 'location' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Konum Bilgileri</h3>
              <CustomDropdown
                value={selectedTimeRange}
                onChange={(value) => setSelectedTimeRange(value)}
                options={timeRangeOptions}
                placeholder="Zaman Aralığı"
                disabled={false}
                className="min-w-0"
              />
            </div>
             
             {/* GNSS Verilerini Kontrol Et */}
             {(() => {
               const gnssData = currentBoat?.gateway_data?.gnss;
               
               if (gnssData && typeof gnssData === 'object') {
                 const latitude = parseCoordinate(gnssData.latitude || gnssData.lat);
                 const longitude = parseCoordinate(gnssData.longitude || gnssData.lng || gnssData.lon);
                 const altitude = parseAltitude(gnssData.altitude || gnssData.alt);
                 const timestamp = gnssData.timestamp || gnssData.time || gnssData.last_updated;
                 
                 // Koordinatların geçerli olup olmadığını kontrol et
                 const isValidLat = latitude !== null && latitude >= -90 && latitude <= 90;
                 const isValidLon = longitude !== null && longitude >= -180 && longitude <= 180;
                 
                 if (isValidLat && isValidLon) {
                   return (
                     <div className="space-y-6">
                                            {/* Harita ve Mevcut Konum */}
                      <LocationMap
                        latitude={latitude}
                        longitude={longitude}
                        altitude={altitude}
                        timestamp={timestamp}
                      />
                       
                      {/* Konum Geçmişi Bileşeni */}
                      <LocationHistory 
                        historicalData={historicalLocationData} 
                        isLoading={isLoadingLocationData}
                        onLoadMore={loadMoreLocationData}
                        hasMore={hasMoreData}
                        isLoadingMore={isLoadingMore}
                        selectedTimeRange={selectedTimeRange}
                        onTimeRangeChange={setSelectedTimeRange}
                      />
                     </div>
                   );
                 } else {
                   return (
                     <div className="text-center py-8">
                       <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                       <h4 className="text-lg font-medium text-gray-900 mb-2">Geçersiz Koordinatlar</h4>
                       <p className="text-gray-600 mb-4">
                         GNSS verileri mevcut ancak koordinatlar geçerli değil.
                       </p>
                       <div className="bg-gray-50 rounded-lg p-4 text-left">
                         <p className="text-sm text-gray-600">
                           <strong>Ham GNSS Verisi:</strong>
                         </p>
                         <pre className="text-xs text-gray-500 mt-2 bg-white p-2 rounded border overflow-auto">
                           {JSON.stringify(gnssData, null, 2)}
                         </pre>
                       </div>
                     </div>
                   );
                 }
               } else {
                 return (
                   <div className="text-center py-8">
                     <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                     <h4 className="text-lg font-medium text-gray-900 mb-2">Konum Verisi Bulunamadı</h4>
                     <p className="text-gray-600">
                       Gateway'den GNSS verisi alınmıyor. GPS sinyali olmayabilir veya GNSS modülü aktif değil.
                     </p>
                   </div>
                 );
               }
             })()}
           </div>
         )}

        {activeTab === 'events' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Olaylar</h3>
              <div className="flex items-center space-x-3">
                <select
                  value={eventTypeFilter}
                  onChange={(e) => setEventTypeFilter(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="all">Tümü</option>
                  <option value="critical">Kritik</option>
                  <option value="warning">Uyarı</option>
                  <option value="info">Bilgi</option>
                </select>
                <select
                  value={eventSourceFilter}
                  onChange={(e) => setEventSourceFilter(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="all">Kaynak: Tümü</option>
                  <option value="gateway">Gateway</option>
                  <option value="sensor">Sensör</option>
                  <option value="system">Sistem</option>
                </select>
                <div className="text-sm text-gray-500">{criticalEvents.length} kayıt</div>
              </div>
            </div>
            {criticalEvents.length === 0 ? (
              <div className="text-center py-12 text-gray-500">Önemli olay kaydı bulunamadı</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zaman</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Olay</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ciddiyet</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kaynak</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {criticalEvents
                      .filter(evt => {
                        if (eventTypeFilter !== 'all' && evt.severity !== eventTypeFilter) return false;
                        if (eventSourceFilter !== 'all') {
                          const src = evt.source?.toLowerCase?.() || '';
                          if (eventSourceFilter === 'gateway' && src !== 'gateway') return false;
                          if (eventSourceFilter === 'system' && src !== 'sistem' && src !== 'system') return false;
                          if (eventSourceFilter === 'sensor' && src === 'gateway') return false;
                        }
                        return true;
                      })
                      .slice(0, eventsDisplayedCount)
                      .map((evt, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(evt.timestamp).toLocaleString('tr-TR')}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{evt.label}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            evt.severity === 'critical' ? 'bg-red-100 text-red-800' :
                            evt.severity === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {evt.severity}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{evt.source}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {eventsDisplayedCount < criticalEvents.filter(evt => {
                  if (eventTypeFilter !== 'all' && evt.severity !== eventTypeFilter) return false;
                  if (eventSourceFilter !== 'all') {
                    const src = evt.source?.toLowerCase?.() || '';
                    if (eventSourceFilter === 'gateway' && src !== 'gateway') return false;
                    if (eventSourceFilter === 'system' && src !== 'sistem' && src !== 'system') return false;
                    if (eventSourceFilter === 'sensor' && src === 'gateway') return false;
                  }
                  return true;
                }).length && (
                  <div className="mt-6 text-center">
                    <button
                      onClick={() => setEventsDisplayedCount(prev => prev + 50)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      50 Kayıt Daha Göster ({criticalEvents.filter(evt => {
                        if (eventTypeFilter !== 'all' && evt.severity !== eventTypeFilter) return false;
                        if (eventSourceFilter !== 'all') {
                          const src = evt.source?.toLowerCase?.() || '';
                          if (eventSourceFilter === 'gateway' && src !== 'gateway') return false;
                          if (eventSourceFilter === 'system' && src !== 'sistem' && src !== 'system') return false;
                          if (eventSourceFilter === 'sensor' && src === 'gateway') return false;
                        }
                        return true;
                      }).length - eventsDisplayedCount} kaldı)
                    </button>
                  </div>
                )}
                <div className="mt-6 text-right">
                  <button
                    onClick={() => {
                      const filtered = criticalEvents.filter(evt => {
                        if (eventTypeFilter !== 'all' && evt.severity !== eventTypeFilter) return false;
                        if (eventSourceFilter !== 'all') {
                          const src = evt.source?.toLowerCase?.() || '';
                          if (eventSourceFilter === 'gateway' && src !== 'gateway') return false;
                          if (eventSourceFilter === 'system' && src !== 'sistem' && src !== 'system') return false;
                          if (eventSourceFilter === 'sensor' && src === 'gateway') return false;
                        }
                        return true;
                      });
                      const excelData = filtered.map((e, i) => ({
                        'Sıra': i + 1,
                        'Zaman': new Date(e.timestamp).toLocaleString('tr-TR'),
                        'Olay': e.label,
                        'Ciddiyet': e.severity,
                        'Kaynak': e.source
                      }));
                      const ws = XLSX.utils.json_to_sheet(excelData);
                      ws['!cols'] = [
                        { width: 8 }, { width: 20 }, { width: 40 }, { width: 12 }, { width: 24 }
                      ];
                      const wb = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(wb, ws, 'Olaylar');
                      const now = new Date();
                      const fileName = `Olaylar_${now.getFullYear()}-${(now.getMonth()+1).toString().padStart(2,'0')}-${now.getDate().toString().padStart(2,'0')}.xlsx`;
                      XLSX.writeFile(wb, fileName);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Excel Dışa Aktar
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Password Change Modal */}
      {showPasswordModal && (
        <PasswordChangeModal 
          onClose={() => setShowPasswordModal(false)}
          currentUser={localStorage.getItem('currentUser')}
        />
      )}
    </div>
  );
};

// Password Change Modal Component
const PasswordChangeModal = ({ onClose, currentUser }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Kullanıcı verilerini localStorage'dan al veya varsayılan değerleri kullan
  const getValidUsers = () => {
    const storedUsers = localStorage.getItem('validUsers');
    if (storedUsers) {
      return JSON.parse(storedUsers);
    }
    // İlk kez çalıştırıldığında varsayılan kullanıcıları oluştur
    const defaultUsers = [
      { username: 'admin', password: 'admin123' },
      { username: 'user', password: 'user123' },
      { username: 'mariot', password: 'mariot2024' }
    ];
    localStorage.setItem('validUsers', JSON.stringify(defaultUsers));
    return defaultUsers;
  };

  const validUsers = getValidUsers();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    // Validasyon
    if (newPassword !== confirmPassword) {
      setError('Yeni şifreler eşleşmiyor!');
      setIsLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('Yeni şifre en az 6 karakter olmalıdır!');
      setIsLoading(false);
      return;
    }

    // Simüle edilmiş API çağrısı
    setTimeout(() => {
      const user = validUsers.find(u => u.username === currentUser);
      
      if (!user) {
        setError('Kullanıcı bulunamadı!');
        setIsLoading(false);
        return;
      }

      if (user.password !== currentPassword) {
        setError('Mevcut şifre hatalı!');
        setIsLoading(false);
        return;
      }

      // Şifreyi gerçekten değiştir
      const updatedUsers = validUsers.map(u => {
        if (u.username === currentUser) {
          return { ...u, password: newPassword };
        }
        return u;
      });
      
      // localStorage'a kaydet
      localStorage.setItem('validUsers', JSON.stringify(updatedUsers));
      
      // Başarı mesajı göster
      setSuccess('Şifre başarıyla değiştirildi!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      setTimeout(() => {
        onClose();
      }, 2000);
      
      setIsLoading(false);
    }, 1000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Şifre Değiştir</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Current Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mevcut Şifre
            </label>
            <div className="relative">
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="block w-full pr-10 pl-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Mevcut şifrenizi girin"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              >
                {showCurrentPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                )}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Yeni Şifre
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className="block w-full pr-10 pl-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Yeni şifrenizi girin"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                )}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Yeni Şifre (Tekrar)
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="block w-full pr-10 pl-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Yeni şifrenizi tekrar girin"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                )}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
              {success}
            </div>
          )}

          {/* Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Değiştiriliyor...
                </div>
              ) : (
                'Şifreyi Değiştir'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Dashboard;
